import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { objectStorageService } from '../../services/objectStorageService';

interface Props {
  projectId: string;
  bucketId: string;
  objectKey: string;
  onClose: () => void;
}

export default function ObjectMetadataPanel({ projectId, bucketId, objectKey, onClose }: Props) {
  const queryClient = useQueryClient();
  const [editMode, setEditMode] = useState(false);
  const [editedMeta, setEditedMeta] = useState<Record<string, string>>({});

  const { data: metadata, isLoading } = useQuery({
    queryKey: ['object-metadata', projectId, bucketId, objectKey],
    queryFn: () => objectStorageService.getObjectMetadata(projectId, bucketId, objectKey),
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      objectStorageService.setObjectMetadata(projectId, bucketId, objectKey, editedMeta),
    onSuccess: () => {
      toast.success('Metadata saved');
      queryClient.invalidateQueries({ queryKey: ['object-metadata', projectId, bucketId, objectKey] });
      setEditMode(false);
    },
    onError: () => toast.error('Failed to save metadata'),
  });

  const startEdit = () => {
    setEditedMeta({ ...(metadata ?? {}) });
    setEditMode(true);
  };

  const updateKey = (oldKey: string, newKey: string) => {
    const updated = { ...editedMeta };
    updated[newKey] = updated[oldKey];
    if (oldKey !== newKey) delete updated[oldKey];
    setEditedMeta(updated);
  };

  const updateValue = (key: string, value: string) => {
    setEditedMeta({ ...editedMeta, [key]: value });
  };

  const removeKey = (key: string) => {
    const updated = { ...editedMeta };
    delete updated[key];
    setEditedMeta(updated);
  };

  const addKey = () => {
    setEditedMeta({ ...editedMeta, '': '' });
  };

  const readonlyKeys = ['content-type', 'size', 'etag', 'last-modified'];

  return (
    <div className="fixed inset-y-0 right-0 z-40 w-96 bg-gray-800 border-l border-gray-700 flex flex-col shadow-2xl">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
        <div>
          <h3 className="text-base font-semibold text-white">Object Metadata</h3>
          <p className="text-xs text-gray-400 mt-0.5 font-mono truncate max-w-64">{objectKey}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-700 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">System</p>
              <div className="space-y-1.5">
                {readonlyKeys.map((k) =>
                  metadata?.[k] !== undefined ? (
                    <div key={k} className="flex items-center justify-between bg-gray-900/60 rounded-lg px-3 py-2">
                      <span className="text-xs text-gray-400 font-mono">{k}</span>
                      <span className="text-xs text-gray-200 font-mono truncate max-w-40 text-right">
                        {metadata[k]}
                      </span>
                    </div>
                  ) : null
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">User Metadata</p>
                {!editMode && (
                  <button onClick={startEdit} className="text-xs text-primary-400 hover:text-primary-300">
                    Edit
                  </button>
                )}
              </div>

              {editMode ? (
                <div className="space-y-2">
                  {Object.entries(editedMeta)
                    .filter(([k]) => !readonlyKeys.includes(k))
                    .map(([k, v], i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <input
                          value={k}
                          onChange={(e) => updateKey(k, e.target.value)}
                          placeholder="key"
                          className="w-1/3 bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white font-mono focus:outline-none focus:border-primary-500"
                        />
                        <input
                          value={v}
                          onChange={(e) => updateValue(k, e.target.value)}
                          placeholder="value"
                          className="flex-1 bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white font-mono focus:outline-none focus:border-primary-500"
                        />
                        <button onClick={() => removeKey(k)} className="text-red-400 hover:text-red-300">
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  <button
                    onClick={addKey}
                    className="flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300 mt-1"
                  >
                    <PlusIcon className="w-3.5 h-3.5" /> Add key
                  </button>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {Object.entries(metadata ?? {})
                    .filter(([k]) => !readonlyKeys.includes(k))
                    .map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between bg-gray-900/60 rounded-lg px-3 py-2">
                        <span className="text-xs text-gray-400 font-mono">{k}</span>
                        <span className="text-xs text-gray-200 font-mono truncate max-w-40 text-right">{v}</span>
                      </div>
                    ))}
                  {Object.keys(metadata ?? {}).filter((k) => !readonlyKeys.includes(k)).length === 0 && (
                    <p className="text-xs text-gray-500 italic">No user metadata</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {editMode && (
        <div className="flex gap-2 px-5 py-4 border-t border-gray-700">
          <button
            onClick={() => setEditMode(false)}
            className="flex-1 py-2 border border-gray-600 text-gray-300 text-sm rounded-lg hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="flex-1 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}
